import { useCallback, useState } from "react";
import { analyzeSentence } from "@/api/sentences";
import type { SentenceAnalysis } from "@/types/sentence";

export interface SentenceAnalysisController {
  isOpen: boolean;
  sentence: string;
  analysis: SentenceAnalysis | null;
  loading: boolean;
  /** Open the analyzer and analyse `sentence`. */
  visualize: (sentence: string) => void;
  close: () => void;
}

/**
 * Sentence-analyzer modal state for a reading. Pass the reading's `isoCode` so
 * the analysis is language-aware.
 */
export function useSentenceAnalysis(isoCode?: string): SentenceAnalysisController {
  const [isOpen, setIsOpen] = useState(false);
  const [sentence, setSentence] = useState("");
  const [analysis, setAnalysis] = useState<SentenceAnalysis | null>(null);
  const [loading, setLoading] = useState(false);

  const visualize = useCallback(
    (selected: string) => {
      setSentence(selected);
      setIsOpen(true);
      setAnalysis(null);
      setLoading(true);
      analyzeSentence(selected, isoCode)
        .then((res) => setAnalysis(res.data.data))
        .catch(() => setAnalysis(null))
        .finally(() => setLoading(false));
    },
    [isoCode],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setSentence("");
    setAnalysis(null);
  }, []);

  return { isOpen, sentence, analysis, loading, visualize, close };
}
